import React, { ChangeEvent, ReactNode } from 'react';
import { CheckSquareIcon, SquareIcon } from '@phosphor-icons/react';
import cn from 'classnames';
import styles from './Checkbox.module.scss';

const LARGE_ICON_SIZE = 20;
const SMALL_ICON_SIZE = 16;

export type CheckboxVariant = 'large' | 'small';

export interface CheckboxProps {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  variant?: CheckboxVariant;
  disabled?: boolean;
  hasError?: boolean;
  errorText?: string;
  className?: string;
  'data-testid'?: string;
  'aria-describedby'?: string;
  /** Optional id for the label element (for aria-describedby from parent) */
  labelId?: string;
}

type CheckboxBaseProps = CheckboxProps & {
  /** When true, label is rendered in a span with aria-labelledby (links do not toggle). When false, label wraps in <label htmlFor>. */
  useRichLabel: boolean;
};

function CheckboxBase({
  id,
  checked,
  onChange,
  label,
  variant = 'large',
  disabled = false,
  hasError = false,
  errorText,
  className,
  'data-testid': dataTestId,
  'aria-describedby': ariaDescribedBy,
  labelId,
  useRichLabel,
}: CheckboxBaseProps) {
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      onChange(e.target.checked);
    }
  };

  const errorId = hasError && errorText ? `${id}-error` : undefined;
  const describedBy =
    [ariaDescribedBy, errorId].filter(Boolean).join(' ') || undefined;

  const isSmall = variant === 'small';
  const iconSize = isSmall ? SMALL_ICON_SIZE : LARGE_ICON_SIZE;
  const checkWeight = isSmall ? 'regular' : 'fill';

  const descriptionId = useRichLabel ? labelId ?? `${id}-label` : undefined;

  return (
    <div
      className={cn(styles.root, className, {
        [styles.large]: variant === 'large',
        [styles.small]: variant === 'small',
      })}
      data-testid={dataTestId}
    >
      <div className={styles.checkboxGroup}>
        <span className={styles.checkboxWrapper}>
          <input
            id={id}
            type="checkbox"
            className={styles.checkboxInput}
            checked={checked}
            onChange={handleChange}
            disabled={disabled}
            aria-describedby={describedBy}
            aria-labelledby={useRichLabel ? descriptionId : undefined}
            aria-invalid={hasError}
          />
          <span className={styles.checkboxIcon} aria-hidden>
            {checked ? (
              <CheckSquareIcon size={iconSize} weight={checkWeight} />
            ) : (
              <SquareIcon size={iconSize} weight="regular" />
            )}
          </span>
        </span>
        {useRichLabel ? (
          <span
            id={descriptionId}
            className={cn(styles.checkboxLabel, styles.richLabel)}
          >
            {label}
          </span>
        ) : (
          <label id={labelId} htmlFor={id} className={styles.checkboxLabel}>
            {label}
          </label>
        )}
      </div>
      {hasError && errorText ? (
        <p id={`${id}-error`} className={styles.errorText} role="alert">
          {errorText}
        </p>
      ) : null}
    </div>
  );
}

const Checkbox = (props: CheckboxProps) => (
  <CheckboxBase {...props} useRichLabel={false} />
);

Checkbox.displayName = 'Checkbox';

function CheckboxWithRichLabel(props: CheckboxProps) {
  return <CheckboxBase {...props} useRichLabel />;
}

CheckboxWithRichLabel.displayName = 'CheckboxWithRichLabel';

export { CheckboxWithRichLabel };
export default Checkbox;
